import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { apiClient } from '../api/client';
import { useAppMode } from './AppModeContext';

interface AuthUser {
  userId: string;
  role: string;
  displayName: string;
  email: string;
  profileSlug: string | null;
  bio: string;
  location: string;
  avatarUrl: string | null;
  aiTier: 'free' | 'pro';
}

export interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    displayName: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  onLogin: (cb: () => void) => () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const loginCallbacks = useRef<Set<() => void>>(new Set());
  const { mode } = useAppMode();

  const fetchMe = useCallback(async () => {
    try {
      const me = await apiClient.getMe();
      setUser({
        userId: me.userId,
        role: me.role,
        displayName: me.displayName,
        email: me.email,
        profileSlug: (me as any).profileSlug ?? null,
        bio: (me as any).bio ?? '',
        location: (me as any).location ?? '',
        avatarUrl: (me as any).avatarUrl ?? null,
        aiTier: (me as any).aiTier ?? 'free',
      });
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // On desktop the embedded axum server needs a moment to start after the
    // Tauri window opens. Retry the /api/auth/me call up to 10 times
    // (5 seconds total) before giving up.
    if (mode !== 'desktop') {
      fetchMe().finally(() => setIsLoading(false));
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const MAX = 10;
    const INTERVAL = 500;

    const tryFetch = async () => {
      try {
        const me = await apiClient.getMe();
        if (cancelled) return;
        setUser({
          userId: me.userId,
          role: me.role,
          displayName: me.displayName,
          email: me.email,
          profileSlug: (me as any).profileSlug ?? null,
          bio: (me as any).bio ?? '',
          location: (me as any).location ?? '',
          avatarUrl: (me as any).avatarUrl ?? null,
          aiTier: (me as any).aiTier ?? 'free',
        });
        setIsLoading(false);
      } catch {
        if (cancelled) return;
        attempts++;
        if (attempts < MAX) {
          setTimeout(tryFetch, INTERVAL);
        } else {
          setUser(null);
          setIsLoading(false);
        }
      }
    };
    tryFetch();

    return () => { cancelled = true; };
  }, [fetchMe, mode]);

  const notifyLoginCallbacks = useCallback(() => {
    loginCallbacks.current.forEach((cb) => cb());
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await apiClient.login({ email, password });
    setUser({
      userId: result.user.id,
      role: result.user.role,
      displayName: result.user.displayName,
      email: result.user.email,
      profileSlug: (result.user as any).profileSlug ?? null,
      bio: '',
      location: '',
      avatarUrl: null,
      aiTier: (result.user as any).aiTier ?? 'free',
    });
    notifyLoginCallbacks();
  }, [notifyLoginCallbacks]);

  const register = useCallback(
    async (data: {
      email: string;
      password: string;
      displayName: string;
    }) => {
      const result = await apiClient.register(data);
      setUser({
        userId: result.user.id,
        role: result.user.role,
        displayName: result.user.displayName,
        email: result.user.email,
        profileSlug: (result.user as any).profileSlug ?? null,
        bio: '',
        location: '',
        avatarUrl: null,
        aiTier: 'free',
      });
      notifyLoginCallbacks();
    },
    [notifyLoginCallbacks]
  );

  const logout = useCallback(async () => {
    await apiClient.logout();
    setUser(null);
    window.location.href = '/';
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchMe();
  }, [fetchMe]);

  const onLogin = useCallback((cb: () => void) => {
    loginCallbacks.current.add(cb);
    return () => { loginCallbacks.current.delete(cb); };
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      register,
      logout,
      refreshUser,
      onLogin,
    }),
    [user, isLoading, login, register, logout, refreshUser, onLogin],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
